import React, { FC, ReactNode, useMemo } from "react";
import { View } from "react-native";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Currency } from "@lookiero/sty-psp-locale";
import { Text } from "@lookiero/sty-psp-ui";
import { CheckoutPricingProjection, CheckoutPricingType } from "../../../../../../projection/pricing/pricing";
import { Price } from "../../../../components/atoms/price/Price";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { style as pricingStyle } from "./Pricing.style";

interface RowProps {
  readonly text: string;
  readonly level?: 1 | 2 | 3;
  readonly children: ReactNode;
  readonly action?: boolean;
}
const Row: FC<RowProps> = ({ action = false, text, level = 3, children }) => {
  const style = useMemo(() => pricingStyle(), []);

  return (
    <View style={style.row}>
      <Text level={level} variant={action ? "action" : "body"}>
        {text}
      </Text>
      {children}
    </View>
  );
};

interface ModifierRowProps {
  readonly translationKey: string;
  readonly amount: number;
  readonly percentage?: number;
  readonly type: CheckoutPricingType;
  readonly currency: Currency;
}

const ModifierRow: FC<ModifierRowProps> = ({ translationKey, amount, percentage, currency, type }) => {
  const modifierText = useI18nMessage({
    domain: DOMAIN,
    id: translationKey,
    values: percentage ? { discount: percentage.toString() } : undefined,
  });

  const displayAmount = type === CheckoutPricingType.DISCOUNT ? -amount : amount;

  return (
    <Row text={modifierText}>
      <Price amount={displayAmount} currency={currency} variant="subtotal" />
    </Row>
  );
};

interface PricingProps {
  readonly pricing: CheckoutPricingProjection;
  readonly totalCheckoutItemsKept: number;
}

const Pricing: FC<PricingProps> = ({ pricing, totalCheckoutItemsKept }) => {
  const { originalPrice, finalPrice, modifiers, currency } = pricing;

  const style = useMemo(() => pricingStyle(), []);

  const totalText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_TOTAL });
  const totalCheckoutItemsKeptText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.SUMMARY_TOTAL_ITEMS_KEPT,
    values: { items: totalCheckoutItemsKept.toString() },
  });
  const subtotalText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_SUBTOTAL });

  return (
    <View>
      <Row text={`${subtotalText} ${totalCheckoutItemsKeptText}`}>
        <Price amount={originalPrice} currency={currency} variant="subtotal" />
      </Row>

      {modifiers.map((modifier, index) => (
        <ModifierRow
          key={`modifier-${index}`}
          amount={modifier.amount}
          currency={currency}
          percentage={modifier.percentage}
          translationKey={modifier.translationKey}
          type={modifier.type}
        />
      ))}

      <View style={style.divider} />

      <Row level={2} text={totalText} action>
        <Price amount={finalPrice} currency={currency} variant="total" />
      </Row>
    </View>
  );
};

export { Pricing };
